import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

interface ChatMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  receiveTime?: number;
  sendTime?: number;
  messageId?: string;
  documents?: any[];
  message_context?: unknown;
  session_context?: unknown;
  [key: string]: unknown;
}

export interface TaskResultAttachment {
  id: string;
  taskName: string;
  taskType: string;
  timestamp: string;
  content: string;
  summary?: string;
  documentId?: string;
}

interface SessionState {
  // Session metadata
  sessionId: string;
  sessionName: string;
  sessionHasBeenSaved: boolean;
  savedFolderForSession: string | null;
  sessionIsShared: boolean;
  isUserSaved: boolean; // Track if session was manually saved by user (vs auto-saved)

  // Auto-save status
  isAutoSaving: boolean;
  autoSaveSuccess: boolean;
  hasUnsavedChanges: boolean;
  saveError: string | null;
  lastSavedAt: number | null;
  sessionRevision: number;

  // Chat state
  chatMessages: ChatMessage[];
  chatInput: string;
  taskAttachments: TaskResultAttachment[];
  sentTaskResults: Record<string, TaskResultAttachment>; // Store sent task results by ID

  // Session documents
  sessionDocuments: unknown[];
  sessionResources: unknown[];
  sessionApplications: any[];
  sessionContext: any; // Session context from localStorage

  // Actions
  setSessionId: (id: string) => void;
  setSessionName: (name: string) => void;
  setSessionHasBeenSaved: (saved: boolean) => void;
  setSavedFolderForSession: (folder: string | null) => void;
  setSessionIsShared: (shared: boolean) => void;
  setIsUserSaved: (saved: boolean) => void; // Mark session as user-saved
  markSessionDirty: () => void;
  beginSessionSave: () => number;
  finishSessionSave: (revision: number) => void;
  failSessionSave: (message: string) => void;

  setChatMessages: (messages: ChatMessage[]) => void;
  addChatMessage: (message: ChatMessage) => void;
  updateChatMessage: (index: number, patch: Partial<ChatMessage>) => void;
  deleteChatMessage: (messageId: number) => void;
  setChatInput: (input: string) => void;

  addTaskAttachment: (attachment: TaskResultAttachment) => void;
  removeTaskAttachment: (attachmentId: string) => void;
  clearTaskAttachments: () => void;
  storeSentTaskResults: (attachments: TaskResultAttachment[]) => void;

  setSessionDocuments: (docs: unknown[]) => void;
  setSessionResources: (resources: unknown[]) => void;
  setSessionApplications: (apps: unknown[]) => void;
  setSessionContext: (context: any) => void;

  // Auto-save unsaved session
  autoSaveSession: () => Promise<void>;

  // Auto-save regular (user-saved) session
  autoSaveRegularSession: () => Promise<void>;

  // Reset session
  resetSession: () => void;
}

// Welcome message that appears as the first message
const WELCOME_MESSAGE: ChatMessage = {
  role: 'assistant',
  content: 'Welcome to Patent Loop! I can help you analyze patents, compare technical documents, review patent regulations, and examine prosecution histories. What would you like to explore?',
  receiveTime: Date.now(),
};

const initialState = {
  sessionId: '',
  sessionName: '',
  sessionHasBeenSaved: false,
  savedFolderForSession: null,
  sessionIsShared: false,
  isUserSaved: false, // New sessions are not user-saved by default
  isAutoSaving: false,
  autoSaveSuccess: false,
  hasUnsavedChanges: false,
  saveError: null,
  lastSavedAt: null,
  sessionRevision: 0,
  chatMessages: [WELCOME_MESSAGE],
  chatInput: '',
  taskAttachments: [],
  sentTaskResults: {},
  sessionDocuments: [],
  sessionResources: [],
  sessionApplications: [],
  sessionContext: {},
};

export const useSessionStore = create<SessionState>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setSessionId: (id) => set(state => state.sessionId === id ? {} : { sessionId: id, isAutoSaving: false, autoSaveSuccess: false, saveError: null, lastSavedAt: null }),
        setSessionName: (name) => set({ sessionName: name }),
        setSessionHasBeenSaved: (saved) => set({ sessionHasBeenSaved: saved }),
        setSavedFolderForSession: (folder) => set({ savedFolderForSession: folder }),
        setSessionIsShared: (shared) => set({ sessionIsShared: shared }),
        setIsUserSaved: (saved) => set({ isUserSaved: saved }),
        markSessionDirty: () => set(state => ({ hasUnsavedChanges: true, autoSaveSuccess: false, sessionRevision: state.sessionRevision + 1 })),
        beginSessionSave: () => {
          const revision = useSessionStore.getState().sessionRevision;
          set({ isAutoSaving: true, autoSaveSuccess: false, saveError: null });
          return revision;
        },
        finishSessionSave: (revision) => set(state => ({
          isAutoSaving: false,
          autoSaveSuccess: true,
          saveError: null,
          lastSavedAt: Date.now(),
          hasUnsavedChanges: state.sessionRevision !== revision,
        })),
        failSessionSave: (message) => set({ isAutoSaving: false, autoSaveSuccess: false, saveError: message, hasUnsavedChanges: true }),

        setChatMessages: (messages) => set(state => ({ chatMessages: messages, chatInput: '', hasUnsavedChanges: false, saveError: null, sessionRevision: state.sessionRevision + 1 })),
        addChatMessage: (message) => set((state) => ({
          chatMessages: [...state.chatMessages, message],
          hasUnsavedChanges: true,
          autoSaveSuccess: false,
          sessionRevision: state.sessionRevision + 1,
        })),
        // One message changed in place, for something that happens to an answer after it
        // has been given — a drawing it offered being made, for instance. The index is
        // the message's position, which is the id the chat already addresses them by.
        updateChatMessage: (index, patch) => set((state) => ({
          chatMessages: state.chatMessages.map((message, position) =>
            position === index ? { ...message, ...patch } : message
          ),
          hasUnsavedChanges: true,
          autoSaveSuccess: false,
          sessionRevision: state.sessionRevision + 1,
        })),
        deleteChatMessage: (messageId) => set((state) => ({
          chatMessages: state.chatMessages.filter((_, index) => index !== messageId),
          hasUnsavedChanges: true,
          autoSaveSuccess: false,
          sessionRevision: state.sessionRevision + 1,
        })),
        setChatInput: (input) => set({ chatInput: input }),

        addTaskAttachment: (attachment) => set((state) => ({
          taskAttachments: [...state.taskAttachments, attachment]
        })),
        removeTaskAttachment: (attachmentId) => set((state) => ({
          taskAttachments: state.taskAttachments.filter((a) => a.id !== attachmentId)
        })),
        clearTaskAttachments: () => set({ taskAttachments: [] }),
        storeSentTaskResults: (attachments) => set((state) => {
          const newSentResults = { ...state.sentTaskResults };
          attachments.forEach(attachment => {
            newSentResults[attachment.id] = attachment;
          });
          return { sentTaskResults: newSentResults };
        }),

        setSessionDocuments: (docs) => set({
          sessionDocuments: docs,
        }),
        setSessionResources: (resources) => set({
          sessionResources: resources,
        }),
        setSessionApplications: (apps) => set({
          sessionApplications: apps,
        }),
        setSessionContext: (context) => set({
          sessionContext: context,
        }),

        autoSaveSession: async () => {
          const state = useSessionStore.getState();

          // Don't auto-save if session has been saved by user (includes legacy sessions)
          if (state.isAutoSaving || state.isUserSaved || state.sessionHasBeenSaved) {
            console.log('[SessionStore] Skipping auto-save: session is user-saved or has been saved');
            return;
          }

          // Check if session is blank (only welcome message)
          if (state.chatMessages.length <= 1) {
            console.log('[SessionStore] Skipping auto-save: session is blank');
            return;
          }

          let savingSessionId = state.sessionId;
          const revision = state.beginSessionSave();
          try {
            const { sessionApi } = await import('@/services/api');
            if (useSessionStore.getState().sessionId !== state.sessionId) return;

            // Generate session ID with "unsaved-" prefix if it doesn't exist
            let sessionId = state.sessionId;
            if (!sessionId) {
              sessionId = `unsaved-${crypto.randomUUID()}`;
              useSessionStore.getState().setSessionId(sessionId);
              console.log('[SessionStore] Generated new unsaved session ID:', sessionId);
            } else if (!sessionId.startsWith('unsaved-')) {
              // If session ID exists but doesn't have unsaved prefix, add it
              sessionId = `unsaved-${sessionId}`;
              useSessionStore.getState().setSessionId(sessionId);
              console.log('[SessionStore] Updated session ID with unsaved prefix:', sessionId);
            }

            const sessionName = state.sessionName || `Unsaved Session - ${new Date().toLocaleDateString()}`;
            savingSessionId = sessionId;
            set({ isAutoSaving: true });

            console.log('[SessionStore] Auto-saving unsaved session...', sessionId);
            await sessionApi.autoSaveUnsavedSession(
              sessionId,
              sessionName,
              state.chatMessages
            );
            console.log('[SessionStore] Auto-save successful');
            if (useSessionStore.getState().sessionId === savingSessionId) {
              state.finishSessionSave(revision);
              window.dispatchEvent(new Event('sessions-updated'));
            }
          } catch (error) {
            console.error('[SessionStore] Auto-save failed:', error);
            if (useSessionStore.getState().sessionId === savingSessionId) state.failSessionSave('Could not save the session backup. Please retry.');
          }
        },

        autoSaveRegularSession: async () => {
          const state = useSessionStore.getState();

          // Only auto-save regular sessions (saved sessions with regular session_id)
          // Skip if session hasn't been saved yet or has no ID
          if (state.isAutoSaving || !state.sessionHasBeenSaved || !state.sessionId || !state.sessionName) {
            console.log('[SessionStore] Skipping regular auto-save: session not saved or incomplete');
            return;
          }

          // Skip if this is an unsaved session (with "unsaved-" prefix)
          if (state.sessionId.startsWith('unsaved-')) {
            console.log('[SessionStore] Skipping regular auto-save: this is an unsaved session');
            return;
          }

          // Skip if session is blank (only welcome message)
          if (state.chatMessages.length <= 1) {
            console.log('[SessionStore] Skipping regular auto-save: session is blank');
            return;
          }

          const revision = state.beginSessionSave();
          try {
            const { sessionApi } = await import('@/services/api');
            if (useSessionStore.getState().sessionId !== state.sessionId) return;

            // Set auto-saving state
            useSessionStore.setState({ isAutoSaving: true, autoSaveSuccess: false });

            console.log('[SessionStore] Auto-saving regular session...', state.sessionId);
            await sessionApi.saveSession(
              state.sessionId,
              state.sessionName,
              state.chatMessages,
              state.savedFolderForSession
            );
            console.log('[SessionStore] Regular auto-save successful');

            // Set success state
            if (useSessionStore.getState().sessionId === state.sessionId) {
              state.finishSessionSave(revision);
              window.dispatchEvent(new Event('sessions-updated'));
            }
          } catch (error) {
            console.error('[SessionStore] Regular auto-save failed:', error);
            if (useSessionStore.getState().sessionId === state.sessionId) state.failSessionSave('Could not save your changes. Please retry.');
          }
        },

        resetSession: () => set({
          ...initialState,
          chatMessages: [{
            ...WELCOME_MESSAGE,
            receiveTime: Date.now(), // Fresh timestamp for new session
          }],
        }),
      }),
      {
        name: 'session-storage',
        partialize: (state) => ({
          chatMessages: state.chatMessages,
          chatInput: state.chatInput,
          hasUnsavedChanges: state.hasUnsavedChanges,
          lastSavedAt: state.lastSavedAt,
          taskAttachments: state.taskAttachments,
          sentTaskResults: state.sentTaskResults,
          sessionId: state.sessionId,
          sessionName: state.sessionName,
          sessionHasBeenSaved: state.sessionHasBeenSaved,
          savedFolderForSession: state.savedFolderForSession,
          isUserSaved: state.isUserSaved,
          sessionContext: state.sessionContext,
        }),
        onRehydrateStorage: () => (state) => {
          // Ensure welcome message is present if chat is empty
          if (state && (!state.chatMessages || state.chatMessages.length === 0)) {
            state.chatMessages = [WELCOME_MESSAGE];
          }

          // If there's a sessionId, it means this session has been saved
          if (state && state.sessionId) {
            state.sessionHasBeenSaved = !state.sessionId.startsWith('unsaved-');
          }

          // Load session context from localStorage if it exists
          if (state) {
            const sessionContextStr = localStorage.getItem('session_context');
            if (sessionContextStr) {
              try {
                state.sessionContext = JSON.parse(sessionContextStr);
              } catch (e) {
                console.error('Failed to parse session_context from localStorage:', e);
                state.sessionContext = {};
              }
            }
          }
        },
      }
    )
  )
);
